import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useEffect, useRef } from 'react';

const AT_BOTTOM_THRESHOLD_PX = 50;

/**
 * Keeps the transcript pinned to its bottom while the reader is there. The shared `useAutoScroll`
 * debounces its follow, and a streaming turn resizes the transcript faster than the debounce, so it
 * never followed until the turn had ended.
 */
export function useStickToBottom(scroller: HTMLElement | null, content: HTMLElement | null) {
  const pinned = useRef(true);
  const lastTop = useRef(0);

  const stick = usePersistentCallback(() => {
    if (!scroller) return;
    scroller.scrollTop = scroller.scrollHeight;
    lastTop.current = scroller.scrollTop;
  });

  const scrollToBottom = usePersistentCallback(() => {
    pinned.current = true;
    stick();
  });

  useEffect(() => {
    if (!scroller) return;
    lastTop.current = scroller.scrollTop;
    const onScroll = () => {
      const top = scroller.scrollTop;
      const atBottom = scroller.scrollHeight - top - scroller.clientHeight <= AT_BOTTOM_THRESHOLD_PX;
      // Content that grew after our own follow leaves the view short of the bottom without the reader
      // moving; only scrolling up lets go.
      if (atBottom) pinned.current = true;
      else if (top < lastTop.current) pinned.current = false;
      lastTop.current = top;
    };
    scroller.addEventListener('scroll', onScroll);
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [scroller]);

  useEffect(() => {
    if (!scroller || !content) return;
    const observer = new ResizeObserver(() => {
      if (pinned.current) stick();
    });
    observer.observe(content);
    // The view shrinks too, when a banner or a question opens above the composer.
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [scroller, content, stick]);

  return { scrollToBottom };
}
